/** Wet combat hexes: the Blackwater surface, and the boards that come soaked.
 *
 * A hex is wet or it is not. The bit lives on `BattleState.wet` so a Leviathan
 * can flood two cells mid-fight and a save paused on a swamp board still knows
 * which puddles were rolled. Grass fights omit the field entirely - optional
 * in the save shape, absent means dry - so a battle that never saw water does
 * not change its hash.
 *
 * Who paints the board:
 *   water / boat  most of the field is shoal (sandbars are the dry holes)
 *   swamp         puddle blobs, about half the interior
 *   river         a 2-hex-wide band crossing the board
 *   none          dry
 */

import { resolveStat } from "../modifiers.js";
import { nextBounded, type RngStreamState } from "../rng/pcg32.js";
import {
  BF_HEIGHT, BF_HEXES, BF_WIDTH, F_SCHOOL, F_UNDEAD, type BattleState,
  hexCol, hexIndex, hexNeighbours, isAlive,
} from "./state.js";

export type WetKind = "none" | "swamp" | "river" | "water";

export function isWet(s: BattleState, hex: number): boolean {
  const w = s.wet;
  if (w === undefined || hex < 0 || hex >= w.length) return false;
  return w[hex] !== 0;
}

export function wetKindFromTerrain(terrain: string, river = false, boat = false): WetKind {
  if (boat) return "water";
  const t = terrain.toLowerCase();
  if (t === "water" || t.endsWith("/bf_water") || t.includes("water")) return "water";
  if (t === "swamp" || t.includes("swamp")) return "swamp";
  if (river) return "river";
  return "none";
}

export const isWetNativeStack = (s: BattleState, slot: number): boolean =>
  (s.stacks.flags[slot]! & F_SCHOOL) !== 0;

/** Connected SCHOOL pack containing `slot` has size >= 3. */
export function schoolPack(s: BattleState, slot: number): boolean {
  if (!isAlive(s.stacks, slot) || !isWetNativeStack(s, slot)) return false;
  const side = s.stacks.side[slot]!;
  const n = s.stacks.n;
  const seen = new Uint8Array(n);
  const q: number[] = [slot];
  seen[slot] = 1;
  let size = 0;
  const hexOf: number[] = [];
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  for (let i = 0; i < n; i++) {
    if (!isAlive(s.stacks, i) || s.stacks.side[i] !== side) continue;
    if ((s.stacks.flags[i]! & F_SCHOOL) === 0) continue;
    hexOf[i] = s.stacks.hex[i]!;
  }
  while (q.length > 0) {
    const cur = q.pop()!;
    size += 1;
    if (size >= 3) return true;
    const hn = hexNeighbours(s.stacks.hex[cur]!, nb);
    for (let k = 0; k < hn; k++) {
      const h = nb[k]!;
      for (let i = 0; i < n; i++) {
        if (seen[i] === 1) continue;
        if (hexOf[i] !== h) continue;
        seen[i] = 1;
        q.push(i);
      }
    }
  }
  return false;
}

export function isFlopped(s: BattleState, slot: number): boolean {
  if (!isAlive(s.stacks, slot) || !isWetNativeStack(s, slot)) return false;
  return !isWet(s, s.stacks.hex[slot]!);
}

export function combatSpeedDelta(s: BattleState, slot: number): number {
  if (!isWetNativeStack(s, slot)) return 0;
  return isWet(s, s.stacks.hex[slot]!) ? 1 : -2;
}

/**
 * A stack's morale or luck as the dice see it: the column the battle opened
 * with, plus every modifier filed under the stat. Mirth, Sorrow, Fortune and
 * Misfortune are `MODIFIER` effects on exactly these keys, and for a long time
 * the two readers below looked at the column alone, so all four spells charged
 * mana and changed nothing.
 */
export function stackFortune(s: BattleState, slot: number, stat: "morale" | "luck"): number {
  const column = stat === "morale" ? s.stacks.morale[slot]! : s.stacks.luck[slot]!;
  const mods = (s.stacks.mods[slot] ?? []).filter((m) => m.stat === stat);
  return mods.length === 0 ? column : resolveStat(column, mods).value;
}

/**
 * Shoal instinct: wet-native stacks on a wet hex get one extra point of luck
 * for the roll, and undead otherwise keep no positive luck.
 */
export function combatLuck(s: BattleState, slot: number): number {
  let l = stackFortune(s, slot, "luck");
  if ((s.stacks.flags[slot]! & F_UNDEAD) !== 0 && l > 0) l = 0;
  if (isWetNativeStack(s, slot) && isWet(s, s.stacks.hex[slot]!)) l += 1;
  if (l > 3) l = 3;
  if (l < -3) l = -3;
  return l;
}

export function floodHex(wet: Uint8Array, hex: number): void {
  if (hex >= 0 && hex < wet.length) wet[hex] = 1;
}

/** Paint `kind` onto a fresh 165-hex mask. Returns the advanced stream. */
export function generateWet(kind: WetKind, rng: RngStreamState): [Uint8Array, RngStreamState] {
  const wet = new Uint8Array(BF_HEXES);
  let st = rng;
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  if (kind === "none") return [wet, st];

  if (kind === "water") {
    wet.fill(1);
    let bars = 0;
    let extra: number;
    [st, extra] = nextBounded(st, 5);
    const target = 8 + extra;
    for (let t = 0; t < 80 && bars < target; t++) {
      let col: number, row: number;
      [st, col] = nextBounded(st, BF_WIDTH - 4);
      [st, row] = nextBounded(st, BF_HEIGHT);
      const n = hexIndex(col + 2, row);
      if (wet[n] === 0) continue;
      wet[n] = 0;
      bars += 1;
      const hn = hexNeighbours(n, nb);
      for (let k = 0; k < hn && bars < target; k++) {
        const h = nb[k]!;
        if (hexCol(h) < 3 || hexCol(h) > BF_WIDTH - 4) continue;
        let coin: number;
        [st, coin] = nextBounded(st, 2);
        if (wet[h] === 1 && coin === 0) {
          wet[h] = 0;
          bars += 1;
        }
      }
    }
    return [wet, st];
  }

  if (kind === "river") {
    let drift: number;
    [st, drift] = nextBounded(st, 3);
    let row = 4 + drift;
    for (let col = 0; col < BF_WIDTH; col++) {
      if (row < 1) row = 1;
      if (row > BF_HEIGHT - 3) row = BF_HEIGHT - 3;
      wet[hexIndex(col, row)] = 1;
      wet[hexIndex(col, row + 1)] = 1;
      if (col > 0 && col < BF_WIDTH - 1) {
        let step: number;
        [st, step] = nextBounded(st, 3);
        if (step === 0 && row > 1) row -= 1;
        else if (step === 2 && row < BF_HEIGHT - 3) row += 1;
      }
    }
    return [wet, st];
  }

  const [st1, extra] = nextBounded(st, 20);
  st = st1;
  const target = 70 + extra;
  let painted = 0;
  for (let t = 0; t < 220 && painted < target; t++) {
    let col: number, row: number;
    [st, col] = nextBounded(st, BF_WIDTH - 4);
    [st, row] = nextBounded(st, BF_HEIGHT);
    const seed = hexIndex(col + 2, row);
    if (wet[seed] === 1) continue;
    wet[seed] = 1;
    painted += 1;
    const hn = hexNeighbours(seed, nb);
    for (let k = 0; k < hn && painted < target; k++) {
      const h = nb[k]!;
      if (hexCol(h) < 2 || hexCol(h) > BF_WIDTH - 3) continue;
      let keep: number;
      [st, keep] = nextBounded(st, 3);
      if (wet[h] === 0 && keep !== 0) {
        wet[h] = 1;
        painted += 1;
      }
    }
  }
  return [wet, st];
}
